import React from "react";
import { useSnackbar } from "notistack";
import { Typography } from "@mui/material";
import { safeArray } from "helpers/utils";

const useAlert = () => {
  const { enqueueSnackbar } = useSnackbar();

  const showErrorMsg = (errorMsg) => {
    return enqueueSnackbar(
      <Typography style={{ fontSize: "1.2rem" }}>{`${errorMsg}`}</Typography>,
      {
        variant: "error",
        preventDuplicate: true,
        anchorOrigin: {
          horizontal: "center",
          vertical: "top",
        },
        autoHideDuration: 10000,
      },
    );
  };

  const showSuccessMsg = (successMsg) => {
    return enqueueSnackbar(
      <Typography style={{ fontSize: "1.2rem" }}>{successMsg}</Typography>,
      {
        variant: "success",
        preventDuplicate: true,
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
        autoHideDuration: 5000,
      },
    );
  };

  // const getErrorMsg = (error) => {
  //   try {
  //     // First check graphQLErrors (common in both types)
  //     if (error?.graphQLErrors && error?.graphQLErrors?.length > 0) {
  //       const errMsgs = safeArray(error?.graphQLErrors)?.map(
  //         (err) => err?.message,
  //       );
  //       return errMsgs.join(", ");
  //     }

  //     // Check networkError and its nested result
  //     if (error?.networkError) {
  //       const networkError = error?.networkError;

  //       // Check if the network error has a result with GraphQL errors (common in 400 responses)
  //       if (
  //         networkError?.result?.errors &&
  //         networkError?.result?.errors?.length > 0
  //       ) {
  //         const errMsgs = safeArray(networkError.result.errors).map(
  //           (err) => err.message,
  //         );
  //         return errMsgs.join(", ");
  //       }

  //       // Return the network error message
  //       return networkError?.message || "Network Error!";
  //     }

  //     // Check for direct error message property
  //     if (error?.message) {
  //       return error.message;
  //     }

  //     // Check for errorMessage property (from ApolloErrorOptions)
  //     if (error?.errorMessage) {
  //       return error.errorMessage;
  //     }

  //     // If error is a direct error response object (like your backend response)
  //     if (
  //       error?.errors &&
  //       Array.isArray(error?.errors) &&
  //       error?.errors?.length > 0
  //     ) {
  //       const errMsgs = safeArray(error.errors).map((err) => err?.message);
  //       return errMsgs.join(", ");
  //     }

  //     return "Something went wrong. Try again!!!";
  //   } catch (err) {
  //     console.error("error from get error func.", err);
  //     return "Something went wrong. Try again!!!";
  //   }
  // };

  const getErrorMsg = (error) => {
    try {
      // Helper to extract message from a single graphQL error object
      const extractMsg = (err) => {
        // Dig into extensions.response.message first (NestJS validation errors)
        const responseMsg = err?.extensions?.response?.message;
        if (responseMsg) {
          if (Array.isArray(responseMsg)) return responseMsg.join(", ");
          if (typeof responseMsg === "string") return responseMsg;
        }
        // Fallback to the top-level message
        return err?.message || null;
      };

      // Check graphQLErrors
      if (error?.graphQLErrors?.length > 0) {
        return safeArray(error.graphQLErrors)
          .map(extractMsg)
          .filter(Boolean)
          .join(", ");
      }

      // Check networkError and its nested result
      if (error?.networkError) {
        const networkError = error.networkError;

        if (networkError?.result?.errors?.length > 0) {
          return safeArray(networkError.result.errors)
            .map(extractMsg)
            .filter(Boolean)
            .join(", ");
        }

        return networkError?.message || "Network Error!";
      }

      // Check for direct error message property
      if (error?.message) return error.message;

      // Check for errorMessage property (from ApolloErrorOptions)
      if (error?.errorMessage) return error.errorMessage;

      // Check for direct errors array (e.g. raw backend response like your case)
      if (Array.isArray(error?.errors) && error.errors.length > 0) {
        return safeArray(error.errors)
          .map(extractMsg)
          .filter(Boolean)
          .join(", ");
      }

      return "Something went wrong. Try again!!!";
    } catch (err) {
      console.error("error from get error func.", err);
      return "Something went wrong. Try again!!!";
    }
  };

  const displayAlert = (type, message) => {
    switch (type) {
      case "success":
        return showSuccessMsg(message);

      case "error":
        if (typeof message === "object") {
          return (message || []).map((msg) => showErrorMsg(msg));
        } else {
          return showErrorMsg(message);
        }

      default:
        break;
    }
  };

  const watchFunction = (successMsg, errorMsg, promise) =>
    promise
      .then((data) => {
        if (!data) {
          displayAlert("error", "Something went wrong, try again.");
          throw Error("Something went wrong in watchFunction");
        }
        displayAlert("success", successMsg);
        return data;
      })
      .catch((error) => {
        const msg = getErrorMsg(error);
        displayAlert("error", msg || errorMsg);
        throw error;
      });

  return { displayAlert, watchFunction, getErrorMsg };
};
export default useAlert;
